import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
    FiUser, 
    FiTag, 
    FiCalendar, 
    FiSearch, 
    FiArrowLeft, 
    FiCheckCircle, 
    FiXCircle 
} from 'react-icons/fi'; // Install react-icons or use your preferred icon set
import apiCall from '../helper/apiCall';

const AssignCoupon = () => {
    const navigate = useNavigate();
    
    // Data States
    const [coupons, setCoupons] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    
    // UI States
    const [isLoading, setIsLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    
    // Form State
    const [formData, setFormData] = useState({
        coupon_code: '',
        coupon_id: '',
        user_id: '',
        valid_at: new Date().toISOString().slice(0, 16)
    });

    useEffect(() => {
        const fetchCoupons = async () => {
            const result = await apiCall('getUserCoupons');
            if (result.success) {
                setCoupons(result.data.filter(c => c.is_active));
            }
        };
        fetchCoupons();
    }, []);

    const handleSearch = async (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (value.length >= 3) {
            setIsSearching(true);
            try {
                const result = await apiCall('searchUsers', { query: value });
                if (result.success) setSearchResults(result.data);
            } catch (err) {
                console.error("Search failed", err);
            } finally {
                setIsSearching(false);
            }
        } else {
            setSearchResults([]);
        }
    };

    const handleUserSelect = (user) => {
        setSelectedUser(user);
        setFormData(prev => ({ ...prev, user_id: user.user_id }));
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const selectedCoupon = coupons.find(c => c.coupon_id.toString() === formData.coupon_id.toString());
            const payload = {
                ...formData,
                coupon_id: parseInt(formData.coupon_id),
                coupon_code: selectedCoupon ? selectedCoupon.coupon_code : '',
                assigned_at: new Date().toISOString(), // Audit timestamp
            };

            const result = await apiCall('assignUserCoupon', payload);
            if (result.success) {
                toast.success("Coupon assigned successfully!");
                navigate('/coupons');
            } else {
                toast.error(result.message || "Failed to assign.");
            }
        } catch (error) {
            toast.error(error.message || 'Internal error.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 pb-12">
            {/* Header Area */}
            <div className="bg-white border-b sticky top-0 z-20 px-4 py-4 sm:px-8">
                <div className="max-w-3xl mx-auto flex items-center justify-between">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                    >
                        <FiArrowLeft className="text-xl text-slate-600" />
                    </button>
                    <h1 className="text-lg font-bold text-slate-800">Assign Coupon</h1>
                    <div className="w-8"></div> {/* Spacer for symmetry */}
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 mt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    
                    {/* STEP 1: SELECT COUPON */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-indigo-100 p-2 rounded-lg">
                                <FiTag className="text-indigo-600" />
                            </div>
                            <h2 className="font-bold text-slate-800">Coupon Details</h2>
                        </div>
                        
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                            Available Coupons
                        </label>
                        <select
                            value={formData.coupon_id}
                            onChange={(e) => setFormData({...formData, coupon_id: e.target.value})}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none cursor-pointer text-slate-700"
                            required
                        >
                            <option value="">Choose a coupon...</option>
                            {coupons.map(c => (
                                <option key={c.coupon_id} value={c.coupon_id}>
                                    {c.coupon_code} — {c.discount_type === 'FIXED' ? `₹${c.discount_value}` : `${c.discount_value}%`} Off
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* STEP 2: SEARCH USER */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-emerald-100 p-2 rounded-lg">
                                <FiUser className="text-emerald-600" />
                            </div>
                            <h2 className="font-bold text-slate-800">Assign To User</h2>
                        </div>

                        {selectedUser ? (
                            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-100 p-4 rounded-xl animate-in fade-in zoom-in duration-300">
                                <div className="flex items-center gap-3">
                                    <FiCheckCircle className="text-emerald-500 text-xl" />
                                    <div>
                                        <p className="font-bold text-emerald-900 leading-none">{selectedUser.full_name}</p>
                                        <p className="text-xs text-emerald-700 mt-1">{selectedUser.mobile_no} • {selectedUser.email}</p>
                                    </div>
                                </div>
                                <button 
                                    type="button" 
                                    onClick={() => { setSelectedUser(null); setFormData({...formData, user_id: ''}); }}
                                    className="p-2 hover:bg-emerald-100 rounded-full text-emerald-600 transition-colors"
                                >
                                    <FiXCircle />
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <FiSearch className="text-slate-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search mobile or email..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-11 pr-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                />
                                {isSearching && (
                                    <div className="absolute right-4 top-3.5 animate-spin h-5 w-5 border-2 border-indigo-500 border-t-transparent rounded-full" />
                                )}

                                {/* Floating Results */}
                                {searchResults.length > 0 && (
                                    <div className="absolute z-30 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-60 overflow-y-auto ring-4 ring-black ring-opacity-5">
                                        {searchResults.map(u => (
                                            <div 
                                                key={u.user_id}
                                                onClick={() => handleUserSelect(u)}
                                                className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0"
                                            >
                                                <p className="text-sm font-bold text-slate-800">{u.full_name}</p>
                                                <p className="text-xs text-slate-500">{u.mobile_no} • {u.email}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* STEP 3: SCHEDULING */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-amber-100 p-2 rounded-lg">
                                <FiCalendar className="text-amber-600" />
                            </div>
                            <h2 className="font-bold text-slate-800">Validity Schedule</h2>
                        </div>
                        
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                            Activate On
                        </label>
                        <input
                            type="datetime-local"
                            value={formData.valid_at}
                            onChange={(e) => setFormData({...formData, valid_at: e.target.value})}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-slate-700"
                            required
                        />
                        <p className="mt-3 text-[11px] text-slate-400 leading-relaxed italic">
                            * The user will see this coupon in their wallet, but will not be able to apply it until the specified time.
                        </p>
                    </div>

                    {/* Submit Button Section */}
                    <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t sm:relative sm:bg-transparent sm:border-0 sm:p-0">
                        <button
                            type="submit"
                            disabled={isLoading || !selectedUser || !formData.coupon_id}
                            className={`w-full py-4 rounded-xl font-bold text-white shadow-xl transition-all transform active:scale-[0.98] ${
                                isLoading || !selectedUser || !formData.coupon_id
                                ? 'bg-slate-300 cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-200'
                            }`}
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Processing...
                                </span>
                            ) : 'Assign Coupon Now'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AssignCoupon;